import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Clock, Copy, ExternalLink, Loader2, Newspaper, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { clipApi, type ClipSummary } from '../api/clipApi'
import { useAuth, useInitializeAuth } from '@/features/auth'
import { toast } from '@/lib/toast'
import { getErrorMessage, isApiError } from '@/lib/axios'
import { useDocumentMeta } from '@/lib/seo'
import { daysUntilExpiry, EXPIRY_WARNING_DAYS, formatCreatedAt } from '../utils/clipExpiry'

/**
 * 「내 클리핑」 — the list of clippings this account has made.
 *
 * Until this existed, a clipping was findable only through the link you had already
 * pasted somewhere. Someone who makes one every morning had no way back to
 * yesterday's, and they vanish at 90 days with no warning, so the countdown and the
 * 「다시 만들기」 action are the point of the screen rather than decoration.
 */
export function MyClipsPage() {
  // The auth listener is started by whichever route is mounted, and this one can be
  // opened directly from a bookmark.
  useInitializeAuth()
  const { isAuthenticated, initialized } = useAuth()
  const queryClient = useQueryClient()

  useDocumentMeta({ title: '내 클리핑', description: '내가 만든 뉴스 클리핑을 모아 봐요.' })

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['my-clips'],
      queryFn: ({ pageParam }) => clipApi.listMyClips({ cursor: pageParam }),
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage.next_cursor,
      enabled: isAuthenticated,
      retry: false,
    })

  /**
   * Make a fresh clipping from an existing one, resetting the 90-day clock.
   *
   * Reads the clipping first, which is also how the screen learns that it expired
   * between the list being drawn and the button being pressed — the list is a
   * snapshot, and the TTL does not wait for it.
   */
  const {
    mutate: recreate,
    isPending: isRecreating,
    variables: recreatingCode,
  } = useMutation({
    mutationFn: async (code: string) => {
      const clip = await clipApi.getClip(code)
      return clipApi.createClip({
        keyword: clip.keyword,
        items: clip.items,
        summary: clip.summary,
      })
    },
    onSuccess: async ({ path }) => {
      const url = `${window.location.origin}${path}`
      await queryClient.invalidateQueries({ queryKey: ['my-clips'] })
      try {
        await navigator.clipboard.writeText(url)
        toast.success('새 클리핑을 만들고 링크를 복사했어요', url)
      } catch {
        toast.success('새 클리핑을 만들었어요', url)
      }
    },
    onError: async (err) => {
      if (isApiError(err) && err.response?.status === 404) {
        // 90일이 지나 이미 사라진 클리핑이다. 목록은 그리던 시점의 스냅샷이라 아직
        // 줄이 남아 있으니, 왜 없어졌는지 말해 주고 목록을 다시 받아 줄을 치운다.
        await queryClient.invalidateQueries({ queryKey: ['my-clips'] })
        toast.error(
          '이 클리핑은 보관 기간 90일이 지나 사라졌어요',
          '목록을 새로 불러왔어요. 기사 목록이 필요하면 키워드로 다시 검색해 주세요.'
        )
        return
      }
      toast.error('클리핑을 다시 만들지 못했어요', getErrorMessage(err))
    },
  })

  const copyLink = async (code: string) => {
    const url = `${window.location.origin}/c/${code}`
    try {
      await navigator.clipboard.writeText(url)
      toast.success('클리핑 링크를 복사했어요', url)
    } catch {
      toast.error('링크를 복사하지 못했어요', url)
    }
  }

  const clips: ClipSummary[] = data?.pages.flatMap((page) => page.items) ?? []

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <header className="mb-6 flex flex-wrap items-center gap-3">
        <Link to="/" className="flex items-center gap-2" aria-label="헤드라이너 홈으로">
          <span className="rounded-lg bg-primary p-2">
            <Newspaper className="h-5 w-5 text-primary-foreground" />
          </span>
          <span className="text-lg font-bold">헤드라이너</span>
        </Link>
        <h1 className="text-lg font-semibold text-muted-foreground">· 내 클리핑</h1>
      </header>

      {!initialized && (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {initialized && !isAuthenticated && (
        <Card>
          <CardContent className="flex flex-col items-start gap-3 pt-6">
            <p className="font-medium">로그인하면 내가 만든 클리핑을 여기서 다시 찾을 수 있어요</p>
            <p className="text-sm text-muted-foreground">
              로그아웃 상태에서 만든 클리핑은 주인이 없어서 목록에 담기지 않아요. 링크를 가진 사람은
              그대로 볼 수 있어요.
            </p>
            <Button asChild>
              <Link to="/">홈에서 로그인하기</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {isAuthenticated && isLoading && (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {isAuthenticated && isError && (
        <Card>
          <CardContent className="pt-6">
            <p className="font-medium">클리핑 목록을 불러오지 못했어요</p>
            <p className="mt-1 text-sm text-muted-foreground">{getErrorMessage(error)}</p>
          </CardContent>
        </Card>
      )}

      {isAuthenticated && !isLoading && !isError && clips.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-start gap-3 pt-6">
            <p className="font-medium">아직 만든 클리핑이 없어요</p>
            <p className="text-sm text-muted-foreground">
              키워드를 검색한 뒤 「클리핑 공유」를 누르면 여기에 쌓여요.
            </p>
            <Button asChild>
              <Link to="/">키워드 검색하러 가기</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {clips.length > 0 && (
        <>
          <p className="mb-3 text-sm text-muted-foreground">
            클리핑은 만든 날부터 90일 동안 보관돼요. 기간이 지나면 링크도 함께 사라져요.
          </p>
          <ul className="space-y-3">
            {clips.map((clip) => {
              const daysLeft = daysUntilExpiry(clip.expires_at)
              const expiringSoon = daysLeft <= EXPIRY_WARNING_DAYS

              return (
                <li key={clip.code}>
                  <Card>
                    <CardContent className="flex flex-wrap items-center gap-x-4 gap-y-3 pt-6">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{clip.keyword}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          기사 <span className="tabular-nums">{clip.item_count}</span>건 ·{' '}
                          {formatCreatedAt(clip.created_at)} · 조회{' '}
                          <span className="tabular-nums">{clip.view_count}</span>회
                        </p>
                        <p
                          className={`mt-1 flex items-center gap-1 text-sm ${
                            expiringSoon ? 'font-medium text-destructive' : 'text-muted-foreground'
                          }`}
                        >
                          <Clock className="h-3.5 w-3.5" />
                          {daysLeft <= 0 ? (
                            '보관 기간이 끝났어요'
                          ) : (
                            <>
                              <span className="tabular-nums">{daysLeft}</span>일 뒤 사라져요
                              {expiringSoon && ' — 필요하면 다시 만들어 주세요'}
                            </>
                          )}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => copyLink(clip.code)}>
                          <Copy className="mr-1.5 h-4 w-4" />
                          링크 복사
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/c/${clip.code}`}>
                            <ExternalLink className="mr-1.5 h-4 w-4" />
                            열기
                          </Link>
                        </Button>
                        <Button
                          variant={expiringSoon ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => recreate(clip.code)}
                          disabled={isRecreating}
                          aria-label={`${clip.keyword} 클리핑 다시 만들기`}
                        >
                          {isRecreating && recreatingCode === clip.code ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="mr-1.5 h-4 w-4" />
                          )}
                          다시 만들기
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </li>
              )
            })}
          </ul>

          {hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}더
                보기
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
